import { Component, computed, input, signal } from '@angular/core';
import { CustomRule } from '../../../models/filter-models';
import { DisplayType, EquipmentTierType } from "../../../models/enums";
import { ItemDataService } from '../../../services/item-data.service';
import { FilterService } from '../../../services/filter.service';
import { ItemData } from '../../../models/item-data-models';
import { FormsModule } from '@angular/forms';
import { AutocompleteComponent, AutocompleteOption } from '../../autocomplete/autocomplete.component';
import { SelectComponent, SelectOptionComponent } from '../select.component';
import { poe1, poe2 } from '../../../../game';
import { BaseComponent } from '../../base.component';
import { logError } from '../../../app.config';
import { GearTierService } from '../../../services/tiering/gear-tier.service';
import { isJewelleryItemClass, JewelleryTierService } from '../../../services/tiering/jewellery-tier.service';
import { sortAutocompleteOptionsByGearTierDesc, sortAutocompleteOptionsByJewelleryTierDesc } from '../../../services/generators/generation-helper.service';

@Component({
  selector: 'app-select-item-and-base-type',
  imports: [FormsModule, AutocompleteComponent, SelectComponent, SelectOptionComponent],
  templateUrl: './select-item-and-base-type.component.html',
  styleUrl: './select-item-and-base-type.component.scss'
})
export class SelectItemAndBaseTypeComponent extends BaseComponent {
  rule = input.required<CustomRule>();
  disabled = input(false);
  cosmetic = input.required<boolean>();

  itemData!: ItemData[];

  currencyImage!: string;
  weaponImage!: string;
  armourImage!: string;
  augmentsImage!: string;
  runesImage!: string;
  gemsImage!: string;
  uncutGemsImage!: string;

  private itemTypeVersion = signal(0);

  metaTypeInfo = computed(() => {
    this.itemTypeVersion();
    switch (this.rule().itemType) {
      case 'All': return 'All items';
      case 'All Augments': return 'All items of class Augment (Runes, Soul Cores, Idols, etc.)';
      case 'All Runes': return 'All Augments with "Rune" in their name';
      case 'All Gems': return 'All Uncut Skill, Support & Spirit Gems and all Skill, Support, Lineage & Kalguuran Gems';
      case 'All Uncut Gems': return 'Uncut Skill, Support & Spirit Gems';
      case 'All Gear': return 'All Armour, Weapons and Offhands';
      case 'All Weapons': return 'All Weapon types';
      case 'All Armour': return 'All Armour types';
      default: return undefined;
    }
  });

  baseTypeAutocompleteOptions = computed((): AutocompleteOption[] => {
    this.itemTypeVersion();
    const itemType = this.rule().itemType;

    if (itemType.includes('All')) {
      return [{ name: 'All' }];
    }

    const foundType = this.itemData.find(i => i.itemType === itemType);
    if (!foundType) {
      logError('Item type not found: ' + itemType + ' - returning All');
      return [{ name: 'All' }];
    }

    if (isJewelleryItemClass(foundType.itemClass)) {
      const tierByName = new Map(
        this.jewelleryTierService.getJewelleryTierData()
          .filter(j => j.itemClass === foundType.itemClass)
          .map(j => [j.name, j.tier]),
      );
      const options: AutocompleteOption[] = foundType.baseTypes.map(bt => ({
        name: bt.name,
        image: bt.image,
        jewelleryTier: tierByName.get(bt.name),
      }));
      return sortAutocompleteOptionsByJewelleryTierDesc(options);
    }

    if (foundType.isGear) {
      const tierByName = new Map(
        this.gearTierService.getGearTierData()
          .filter(g => g.itemClass === foundType.itemClass)
          .map(g => [g.name, g.tier]),
      );
      const options: AutocompleteOption[] = foundType.baseTypes.map(bt => ({
        name: bt.name,
        image: bt.image,
        gearTier: tierByName.get(bt.name),
      }));
      return sortAutocompleteOptionsByGearTierDesc(options);
    }

    return foundType.baseTypes.map(bt => ({ name: bt.name, image: bt.image }));
  });

  constructor(
    private itemDataService: ItemDataService,
    private filterService: FilterService,
    private gearTierService: GearTierService,
    private jewelleryTierService: JewelleryTierService,
  ) {
    super();
    this.itemData = this.itemDataService.getItemData();
    const exampleCurrency = poe1 ? 'Chaos Orb' : 'Exalted Orb';
    const exampleWeapon = poe1 ? 'One Hand Swords' : 'Two Hand Maces';
    this.currencyImage = this.itemData.find(i => i.itemType === 'Currency')!.baseTypes.find(b => b.name === exampleCurrency)!.image!;
    this.weaponImage = this.itemData.find(i => i.itemClass === exampleWeapon)!.baseTypes[0].image!;
    this.armourImage = this.itemData.find(i => i.itemClass === 'Body Armours')!.baseTypes[0].image!;
    if (poe2) {
      this.augmentsImage = '/img/all-augments.png';
      this.runesImage = '/img/all-runes.png';
      this.gemsImage = '/img/all-gems.png';
      this.uncutGemsImage = '/img/all-uncut-gems.png';
    }
  }

  itemTypeChanged() {
    const rule = this.rule();
    rule.filterItemBaseType = undefined;
    const itemData = this.itemData.find(i => i.itemType === rule.itemType);
    if (itemData) {
      rule.itemClass = itemData.itemClass;
      rule.filterItemClass = itemData.filterItemClass;
    } else if (rule.itemType === 'All Gear') {
      rule.filterItemClass = this.itemDataService.getAllWeaponTypes().map(g => `"${g}"`).join(' ') + ' ' + this.itemDataService.getAllArmourTypes().map(g => `"${g}"`).join(' ');
      rule.itemClass = 'All';
    } else if (rule.itemType === 'All Weapons') {
      rule.filterItemClass = this.itemDataService.getAllWeaponTypes().map(g => `"${g}"`).join(' ');
      rule.itemClass = 'All';
    } else if (rule.itemType === 'All Armour') {
      rule.filterItemClass = this.itemDataService.getAllArmourTypes().map(g => `"${g}"`).join(' ');
      rule.itemClass = 'All';
    } else if (rule.itemType === 'All Augments') {
      rule.filterItemClass = '"Augment"';
      rule.itemClass = 'All';
    } else if (rule.itemType === 'All Runes') {
      rule.filterItemClass = '"Augment"';
      rule.filterItemBaseType = '"Rune"';
      rule.itemClass = 'All';
    } else if (rule.itemType === 'All Gems') {
      rule.filterItemClass = this.itemDataService.getAllGemFilterClasses();
      rule.itemClass = 'All';
    } else if (rule.itemType === 'All Uncut Gems') {
      rule.filterItemClass = this.itemDataService.getAllUncutGemFilterClasses();
      rule.itemClass = 'All';
    } else {
      rule.itemClass = 'All';
      rule.filterItemClass = undefined;
    }

    if (itemData && itemData.baseTypes.length === 1) {
      rule.baseTypes = [itemData.baseTypes[0].name];
    } else {
      rule.baseTypes = ['All'];
    }

    if (!rule.itemType.includes('All') && rule.advancedConditions && rule.advancedConditions.classes) {
      rule.advancedConditions.classes = undefined;
    }

    if (!this.cosmetic()) {
      if (rule.itemType === 'All Augments' || rule.itemType === 'All Runes'
          || rule.itemType === 'All Gems' || rule.itemType === 'All Uncut Gems') {
        rule.displayOptions.displayType = DisplayType.Tier;
        rule.displayOptions.displayTypeTierHighlightType = undefined;
      } else if (rule.itemType === 'Skill Gems' || rule.itemType === 'Support Gems') {
        rule.displayOptions.displayType = DisplayType.EquipmentTier;
        rule.displayOptions.displayTypeTierHighlightType = EquipmentTierType.Gem;
      } else if (rule.itemType === 'Waystones' || rule.itemType === 'Maps') {
        rule.displayOptions.displayType = DisplayType.EquipmentTier;
        rule.displayOptions.displayTypeTierHighlightType = EquipmentTierType.Map;
      } else if (itemData && itemData.isCurrency) {
        rule.displayOptions.displayType = DisplayType.Tier;
        rule.displayOptions.displayTypeTierHighlightType = undefined;
      } else {
        rule.displayOptions.displayType = DisplayType.EquipmentTier;
        rule.displayOptions.displayTypeTierHighlightType = EquipmentTierType.Item;
      }
    }

    this.itemTypeVersion.update(v => v + 1);
    this.updateFilter();
  }

  baseTypesChanged(values: string[]) {
    const rule = this.rule();
    rule.baseTypes = values;
    if (!values.includes('All') && rule.advancedConditions && rule.advancedConditions.baseTypes) {
      rule.advancedConditions.baseTypes = undefined;
    }
    this.updateFilter();
  }

  updateFilter() {
    this.filterService.updateFilter();
  }
}
